import React, {
  ImgHTMLAttributes,
  RefAttributes,
  SourceHTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { vars } from '@croquiscom-pvt/zds/tokens';
import { themeTokens } from '@croquiscom-pvt/zds/tokens/zigzag';
import { css, keyframes } from '@emotion/react';
import clsx from 'clsx';
import compact from 'lodash/compact';

import { modifyQueryParams } from '@domains/pdp-common';

interface ProgressiveImageProps {
  high_quality_image: string;
  low_quality_image: string;
  disable_skeleton?: boolean;
  is_static?: boolean;
  use_gif?: boolean;
  alt?: string;
  onLoad?: () => void;
}

export function ProgressiveImage({
  high_quality_image,
  disable_skeleton = false,
  low_quality_image,
  is_static,
  use_gif,
  onLoad,
  alt,
}: ProgressiveImageProps) {
  const [showSkeleton, setShowSkeleton] = useState(!disable_skeleton);

  const { imageProps } = useImage({
    src: low_quality_image,
    alt: alt ?? '',
    loading: 'lazy',
    onLoad: () => {
      setShowSkeleton(false);
      onLoad?.();
    },
    // 에러일 경우에도 스켈레톤을 숨기기 위해 onError를 사용합니다.
    onError: () => setShowSkeleton(false),
  });

  const image_sources = useMemo(() => {
    const enable_webp = !use_gif || !is_static;

    return compact([
      enable_webp && {
        srcSet: modifyQueryParams(high_quality_image, {
          format: 'webp',
        }),
        type: 'image/webp',
      },
      {
        srcSet: high_quality_image,
        type: 'image/jpeg',
      },
    ]) as Array<SourceHTMLAttributes<HTMLSourceElement>>;
  }, [is_static, use_gif, high_quality_image]);

  return (
    <div css={wrapperCss} className={clsx([showSkeleton && 'show-skeleton'])}>
      <picture>
        {image_sources.map((source) => (
          <source key={`image-source-${source.srcSet}`} {...source} />
        ))}
        <img {...imageProps} alt={alt} />
      </picture>
    </div>
  );
}

const shine = keyframes`
  to {
    background-position: 100vw 0, 0 0;
  }
`;

const wrapperCss = css`
  position: relative;
  height: 100%;

  :root &,
  :root[data-theme='light'] &,
  .light-theme & {
    --effect-color: ${themeTokens.v3Color.gray50.value.light};
  }

  :root &,
  :root[data-theme='dark'] &,
  .dark-theme & {
    --effect-color: ${themeTokens.v3Color.gray200.value.dark};
  }

  &.show-skeleton {
    background: linear-gradient(0.25turn, transparent, var(--effect-color), transparent),
      linear-gradient(${vars.v3Color.gray150}, ${vars.v3Color.gray150});
    background-repeat: no-repeat;
    background-size: 100%;
    background-position: -100vw 0, 0 0;
    animation: ${shine} 1.8s 0.05s infinite;
  }
`;

type UseImageStatus = 'loading' | 'failed' | 'pending' | 'loaded';

interface UseImageOptions {
  src: string;
  alt: string;
  loading?: React.ImgHTMLAttributes<HTMLImageElement>['loading'];
  crossOrigin?: React.ImgHTMLAttributes<HTMLImageElement>['crossOrigin'];
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  onError?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}

// Reference: https://github.com/croquiscom/zds/blob/b284c2681aab5986663d596507d7e3c8962900bc/packages/components/src/product-card/Thumbnail.tsx#L72-L73
function useImage(options: UseImageOptions) {
  const { src, alt, loading, crossOrigin, onLoad, onError } = options;
  const [status, setStatus] = useState<UseImageStatus>(src && src !== '' ? 'loading' : 'pending');
  const srcRef = useRef<string | null>(src);

  const imageRef = useCallback((node: HTMLImageElement | null) => {
    if (!node) return;

    // SSR에서 이미지 캐시가 있는 경우 img 속성에 onLoad 및 onError를 호출하지 않으므로 complete 및 naturalHeight를 통해 상태를 감지합니다.
    // 참고: https://github.com/facebook/react/issues/15446#issuecomment-484709466
    const isSSR = node.complete;
    const isErrorFromSSR = isSSR && node.naturalHeight === 0;

    if (isSSR) {
      setStatus('loaded');
    }
    if (isErrorFromSSR) {
      setStatus('failed');
    }
  }, []);

  const imageStatusProps = {
    'data-loaded': status === 'loaded',
  };

  const imageProps: ImgHTMLAttributes<HTMLImageElement> & RefAttributes<HTMLImageElement> = {
    ref: imageRef,
    src,
    alt,
    loading,
    crossOrigin,
    ...imageStatusProps,
    onLoad: (event) => {
      setStatus('loaded');
      onLoad?.(event);
    },
    onError: (event) => {
      setStatus('failed');
      onError?.(event);
    },
  };

  useEffect(() => {
    if (srcRef.current !== src) {
      setStatus(src ? 'loading' : 'pending');
    }
    srcRef.current = src;
  }, [src]);

  return { imageProps, status };
}
